import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  ClosedSession,
  CurrentSession,
  MyAttendance,
  ScheduledSession,
} from '@contracts/index';

import { ApiError, api } from '../api';
import {
  capitalised,
  formatAppointment,
  formatDayMonth,
  formatLongDate,
  formatTime,
  formatWeekdayDate,
  joinDates,
  missesInARow,
  plural,
  relativeDay,
  whenNextDay,
  words,
} from '../format';
import { ChevronDownIcon, ChevronRightIcon, PlusIcon, WarningIcon } from '../icons';
import { useWake } from '../liveness';
import { needingReview } from '../review';
import Ask from './Ask';
import GatheringForm, { blankPlan, type Plan } from './GatheringForm';
import type { Open } from './SessionsScreen';
import { Empty, Failed, Settling, Skeleton } from './States';
import Unfold from './Unfold';
import { type Held } from './WhenField';

/**
 * The list of a choir's gatherings. Ported from
 * `legacy/frontend/src/screens/ScheduleScreen.tsx` at legacy commit 2953e3f.
 *
 * **Arriving in pieces, in the order of `docs/specs/features/F-004/plan.md` §3.** What is
 * here is T11 — request 1 and the `Row` every block is made of — T12: the partition, the
 * three blocks and the running row — T13: the archive, its fold and its second page —
 * T14: the three states, both empty conditions, and the `failure` request 1 records —
 * T15: the capsule, and the `!empty` that decides where it is drawn — and T19: the
 * machine-closed alert at the top, with `../review.ts` under it — T20: request 2 and
 * the two-rehearsal rule it draws — and T21: «Твоя проповедь», the last of the three
 * cards above the list, with `act`, `refusal` and the question that frees the date —
 * and T21.5: `useWake(() => void load())` (`:313-317`), the one reload that skips the
 * reset, its own task by the human's decision on `FND-039` because no other task in the
 * plan claimed it.
 *
 * **What `adding` opens arrived with F-005 T7** — `docs/specs/features/F-005/plan.md` §3.
 * Both ways in are F-004's and untouched by it — the button inside the empty card (`:526`)
 * and the capsule (`:703`) — and what they now turn on is the real `GatheringForm`, with
 * `held` (`:302-311`) and `add(plan)` (`:352-381`) under it. What is still absent from this
 * file of the old one is nothing this flow owns.
 *
 * That is a partial port and not a parity claim — no baseline is compared against this
 * file before T23.
 */

/** The date line over a title, and its colour: due today, overdue, or neither. */
function dateLineClass(tone: 'soon' | 'overdue' | null): string {
  return tone === null ? 'date-line' : `date-line date-line--${tone}`;
}

function isPast(item: ScheduledSession): boolean {
  const when = item.session.scheduled_start;
  return when !== null && new Date(when).getTime() < Date.now();
}

/**
 * Ported as it stands, including the comparison against the word the date line prints —
 * FND-023, `KEEP`. A date comparison here would look like a tidy-up and be a behavioural
 * change in every timezone edge case; `SessionsScreen.test.tsx` holds a case that fails
 * on exactly that rewrite.
 */
function isToday(item: ScheduledSession): boolean {
  const when = item.session.scheduled_start;
  return when !== null && relativeDay(when) === 'сегодня';
}

/**
 * One evening, as one row, and every row in this list is the same shape.
 *
 * The chevron points right, which in this app is the promise of another screen
 * — and that is what it is now: what used to unfold into a card in place is a
 * screen about that evening. The row carries the date, the name, and nothing
 * else. The answer counts used to stand in it and cost the date line 98px, and
 * the date is what this list is scanned by; the counts are on the screen the
 * row leads to.
 */
function Row({
  tone,
  when,
  title,
  live,
  past,
  onOpen,
}: {
  tone: 'soon' | 'overdue' | null;
  when: string;
  title: string;
  /** It is happening now: the same row in a gold edge, and nothing else. */
  live?: boolean;
  /**
   * It is behind today. «через 3 дн.» is what the date line adds where there is
   * something to wait for; on the tail of the list the same clause would say
   * «сегодня» about a gathering that ended this morning.
   */
  past?: boolean;
  onOpen: () => void;
}) {
  const relative = past === true || tone !== null ? null : relativeDay(when);
  return (
    <button className={live ? 'tile card--live' : 'tile'} onClick={onOpen}>
      <span className="tile-body">
        <span className={dateLineClass(tone)}>
          {formatWeekdayDate(when)} · <span className="num">{formatTime(when)}</span>
          {relative !== null && ` · ${relative}`}
        </span>
        <span className="tile-title">{title}</span>
      </span>
      <ChevronRightIcon size={20} quiet />
    </button>
  );
}

/**
 * C-016 (FND-1611): whether `RuleBanner` draws anything, read by the banner and by the
 * fold around it, which must not stand open and empty. The banner's own test was
 * `mine.misses_now === 0` after the `barred` arm; this is the same two questions.
 */
function ruleApplies(mine: MyAttendance): boolean {
  return mine.barred || mine.misses_now !== 0;
}

/**
 * The two-rehearsal rule, above the list, in the two shapes it has.
 *
 * The first block of the tab everybody opens without meaning to. No button in
 * either state — «Приду» is on the gathering's own screen, and two identical
 * actions on one screen is what this interface has been removing.
 *
 * Both states are drawn from the live run, so both go of their own accord the
 * moment the person turns up at the next rehearsal — and both name that
 * rehearsal, because it is the way out of the state the card is about. The bar
 * used to be drawn from a latched figure and stood over somebody the rule had
 * already let back in; see `domain/misses.py`. Ported from legacy
 * `ScheduleScreen.tsx:95-148` at `d5aa0fa` — `FND-915`, the range corrected by `FND-867`.
 */
function RuleBanner({ mine, nextRehearsal }: { mine: MyAttendance; nextRehearsal: string | null }) {
  const service =
    mine.next_service_start === null
      ? 'ближайшее участие'
      : `участие ${formatLongDate(mine.next_service_start)}`;

  if (mine.barred) {
    return (
      <section className="card card--gathering card--toned" data-tone="danger">
        <p className="label label--tone">{missesInARow(mine.misses_streak)}</p>
        <h2 className="card-title">На {service} ты не идёшь</h2>
        <p className="hint">
          Пропущены {joinDates(mine.missed_starts)}.{' '}
          {nextRehearsal === null
            ? 'Придёшь на следующую спевку'
            : `Придёшь ${whenNextDay(nextRehearsal)}`}{' '}
          — и на {service} идёшь как обычно.
        </p>
      </section>
    );
  }

  if (!ruleApplies(mine)) return null;
  return (
    <section className="card card--gathering">
      <p className="date-line">пропущена спевка {formatDayMonth(mine.missed_starts[0])}</p>
      <h2 className="card-title">
        {nextRehearsal === null
          ? 'Ждём тебя на следующей'
          : `Ждём тебя ${whenNextDay(nextRehearsal)}`}
      </h2>
      <p className="hint">Ещё один пропуск подряд — и на {service} ты не идёшь.</p>
    </section>
  );
}

/**
 * The choir's gatherings as one list, running «Не закрыто» → «Сегодня» →
 * ahead → «Прошедшие».
 *
 * It was two lists on two tabs: the schedule here and «Что уже прошло» behind
 * «Посещаемость» on «Хор». They are the same cards, and the only thing that
 * differs is which side of today they are on — so the list simply goes on
 * downwards, and the hub that held the second one has one page fewer.
 *
 * «Не закрыто» is unfinished work rather than the day: a scheduled gathering
 * whose time has passed and nobody closed used to stand under «Сегодня»,
 * where it read as "a lot of open rehearsals" rather than as the two or three
 * genuinely unclosed ones it usually is. «Прошедшие» is folded shut by
 * default and paged rather than loaded whole — a choir with a year behind it
 * has too much history for one response, and an archive is not a work
 * surface a regent opens on every visit anyway.
 *
 * **The old file's fifth paragraph is deliberately absent** — «a singer's tail is empty on
 * purpose: `GET /session/history` is the regent's». It is false: `:319-332` loads the
 * history for everybody, and the server answers a singer `total: 1`. `FND-022`, decided
 * `KEEP` on 2026-09-08 — «the rebuild copies the code, not the docstring». This file had it
 * backwards until 2026-09-09: the false sentence was ported and the four true paragraphs
 * above were dropped, which is the opposite of what was decided. The correction that used to
 * stand alone thirty lines below, on the `history` state, stays where it is.
 */
export default function ScheduleScreen({
  isRegent,
  session,
  reloadKey,
  onOpen,
  onSessionChanged,
}: {
  isRegent: boolean;
  /** The gathering that is running, which is the first row of the list. */
  session: CurrentSession | null | undefined;
  /**
   * Bumped when something is opened, closed, answered or removed. The list was
   * loaded before any of that.
   */
  reloadKey: number;
  onOpen: (open: Open) => void;
  /**
   * Something the list did changed a gathering, so the shell's own poll is behind. The
   * one caller here is `act` (`:326`); the other in the old app is `add`, which is
   * F-005's.
   */
  onSessionChanged: () => void;
}) {
  const [schedule, setSchedule] = useState<ScheduledSession[] | null>(null);
  /**
   * What the choir has already held, oldest-loaded last. Regent only, and one
   * or more pages concatenated rather than filtered on arrival: the archive
   * wants all of what is loaded, the alert wants the ones the scheduler
   * closed, and the date field wants the weekday and the hour this choir
   * keeps. `historyTotal` and `needsReviewCount` hold over the whole history,
   * not just what is loaded - see `SessionHistoryPage` on the server.
   *
   * «Regent only» is the docstring's claim and not the code's: the endpoint became every
   * member's on 2026-09-07 and this screen loads it for a singer too — FND-022, and the
   * `singer` baseline shows «Прошедшие · 2».
   */
  const [history, setHistory] = useState<ClosedSession[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  /**
   * The scheduler's closings over the whole history, which is the badge on the alert
   * above the list. Set here because request 3 answers with it and the three values move
   * together (`:266-270`).
   *
   * It counts a different set from the one the alert's own sentence is about, on purpose —
   * `spec.md` §1.9: this is the server's count over the whole history within 14 days
   * (`sessions.py:1464-1474`), while the subject is the head of what is **loaded**.
   */
  const [needsReviewCount, setNeedsReviewCount] = useState(0);
  /** The archive opens to nothing every time: it is not a work surface. */
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [historyLoadingMore, setHistoryLoadingMore] = useState(false);
  /**
   * Where the person stands under the two-rehearsal rule.
   *
   * The one request this screen makes that is not about the schedule, and it is
   * the same one «Я» makes: the card the rule earns stands here, on the tab
   * everybody opens without meaning to. Null while it is in flight or if it
   * failed — a card about a rule is not worth a refusal on a screen about
   * dates.
   *
   * Which is to say the two states are one value on purpose, and AC-7 and AC-50 both
   * rest on their being indistinguishable on screen.
   */
  const [mine, setMine] = useState<MyAttendance | null>(null);
  /** The sermon being handed back, by session id, while the question stands. */
  const [declining, setDeclining] = useState<string | null>(null);
  /**
   * A gathering being planned. The sheet it opens is **F-005**; what is here is the pair of
   * conditions the old app writes around it — `{empty && !adding}` and
   * `{nothingAhead && !adding}` (`:511`, `:534`) — and the two controls that turn it on: the
   * button inside the empty card (`:526`) and the capsule (`:703`).
   */
  const [adding, setAdding] = useState(false);
  /**
   * Where a refusal goes, which is where the tap was: `'screen'`, `'form'` or
   * `` `sermon:<id>` `` (`:185-188`, `:334-337`). `'form'` is F-005's; of the two that
   * are here, only `` `sermon:<id>` `` is ever read as words — `'screen'` is read by
   * `.at` alone, because `Failed` says its own, so the `text` request 1 records is never
   * drawn. Ported as it stands; see the note in `load` below.
   */
  const [failure, setFailure] = useState<{ at: string; text: string } | null>(null);
  /** A request is out, and every button that could start another one is off until it lands. */
  const [busy, setBusy] = useState(false);

  // Answers with what it loaded as well as drawing it: creating a gathering ends
  // on that gathering's own screen, and the screen wants the row rather than the
  // brief the POST returns - the counts and the answers are on the row.
  const load = useCallback(async (): Promise<ScheduledSession[] | null> => {
    try {
      const loaded = isRegent ? await api.schedule() : await api.upcoming();
      setSchedule(loaded);
      setFailure(null);
      return loaded;
    } catch (error) {
      // `at: 'screen'` draws the refusal card at the top, and only while there
      // is no schedule to keep: a reload that fails over a list already drawn
      // leaves the list alone and says nothing at all.
      //
      // The `text` is built here and read nowhere: `refusal()` (`:334-337`) is called for
      // `` `sermon:<id>` `` alone (`:499`), and the card `at: 'screen'` draws says
      // «Расписание не пришло» in its own words. Ported as it stands — AC-10 says the
      // message is «rendered», and in the old app it is not.
      setFailure({
        at: 'screen',
        text: error instanceof ApiError ? error.message : 'Не удалось загрузить расписание',
      });
      return null;
    }
  }, [isRegent]);

  /**
   * Whose schedule is on screen: the `load` that fetched it, which changes with the role.
   * C-023 (`FND-1618`): the reset below used to run on every pass of this effect, and
   * since the screen now stays mounted under a pushed evening and under another tab, the
   * effect also runs again on every return — a skeleton over a list that was already
   * there, which is the jerk the audit of 2026-10-27 measured. The reset is for a role
   * that changed; a return and a reload refresh the list in place.
   */
  const loadedBy = useRef<typeof load | null>(null);
  useEffect(() => {
    // Reset before loading: switching choirs can change the role, and without
    // this the previous role's schedule would stay on screen.
    //
    // The reset is the skeleton path and is what AC-2 asks for, so it stays where the old
    // app has it (`ScheduleScreen.tsx:239-241`) rather than being derived during render:
    // what it synchronises with is the server, and the role that decides which server
    // call to make.
    // oxlint-disable-next-line react/set-state-in-effect
    if (loadedBy.current !== load) setSchedule(null);
    loadedBy.current = load;
    // The linter reads `load()`'s `setSchedule` after its `await` as this effect's own.
    // oxlint-disable-next-line react/set-state-in-effect
    void load();
  }, [load, reloadKey]);

  /**
   * Request 2, and **the request is the instrumentation**: the server writes a
   * `screen_views` row in `_note_view` before the response is built
   * (`legacy/backend/app/api/stats.py:375`, `:268`), and that series is the whole of the
   * product's usage history. It is one of only two endpoints that ever write one, so
   * this must be issued in the same situations as the old app and no others — calling it
   * more often, less often or on a different trigger corrupts the record silently, which
   * is `docs/release/CUTOVER.md` item 4.
   *
   * Hence the dependency list is `[reloadKey]` and nothing else, exactly as at
   * `legacy/frontend/src/screens/ScheduleScreen.tsx:246-252`.
   *
   * The `catch` sets `null`, the same value the state starts at: a failure is
   * indistinguishable from a flight, and neither says anything on screen.
   */
  useEffect(() => {
    void api
      .myAttendance('rehearsal')
      .then(setMine)
      .catch(() => setMine(null));
  }, [reloadKey]);

  // Everybody, and the regent alone until 2026-09-07: a singer had no way to
  // reach a gathering that is over, so what the choir sang at a service they
  // stood in was unreachable and the number they signed up there could not be
  // corrected. What the closed screen offers them is narrower — the roster and
  // the two buttons that change an evening are behind `isRegent` inside it.
  useEffect(() => {
    void api
      .history()
      .then((page) => {
        setHistory(page.sessions);
        setHistoryTotal(page.total);
        setNeedsReviewCount(page.needs_review_count);
      })
      .catch(() => {
        setHistory([]);
        setHistoryTotal(0);
        setNeedsReviewCount(0);
      });
  }, [reloadKey]);

  /**
   * One more page of the archive, behind the loaded tail's own date.
   *
   * A button and not a scroll listener: a scroll past the archive's bottom
   * asking the server for more on its own is work done without being asked,
   * and it is the same reasoning `.fab` already carries about the schedule
   * above it.
   */
  const loadMoreHistory = useCallback(async () => {
    const oldest = history[history.length - 1];
    if (oldest === undefined) return;
    setHistoryLoadingMore(true);
    try {
      const page = await api.history({ before: oldest.effective_start });
      setHistory((loaded) => [...loaded, ...page.sessions]);
      setHistoryTotal(page.total);
      setNeedsReviewCount(page.needs_review_count);
    } catch {
      // DEV-1 · FND-019, the slice's one approved deviation. The old app has `try`/`finally`
      // and no `catch` here (`:281-293`), and is called as `void loadMoreHistory()` (`:665`),
      // so a failed second page leaks an unhandled rejection into the error tracker
      // (ADR-0010). The catch is **silent**, by the human's decision: the loaded pages
      // stand, the `finally` below un-disables the button, and nothing is said — which is
      // exactly what the screen does today. No new copy, no refusal, no retry affordance.
    } finally {
      setHistoryLoadingMore(false);
    }
  }, [history]);

  const review = useMemo(() => needingReview(history), [history]);

  /**
   * Everything the choir has held and everything it has planned, for the date
   * the form offers. Both halves, because a habit is as visible in a rehearsal
   * already booked as in one already sung.
   *
   * The two halves take their start from **different fields** on purpose — a closed
   * gathering is remembered by when it actually ran, a planned one by when it is meant to
   * (`docs/specs/legacy/F-005/spec.md` §7.12) — and the sample is whatever `history`
   * already holds: nothing here asks for a second page to widen it, so a recent gathering
   * pushed off page one is invisible to the suggestion. `FND-077`, `KEEP`.
   */
  const held = useMemo<Held[]>(
    () => [
      ...history.map((one) => ({ kind: one.kind, start: one.effective_start })),
      ...(schedule ?? []).map((one) => ({
        kind: one.session.kind,
        start: one.session.scheduled_start ?? one.session.effective_start,
      })),
    ],
    [history, schedule],
  );

  // The schedule is loaded once, so one made while the app sat in a pocket
  // never arrived until a restart. Re-read in place, without the reset above:
  // a skeleton flashing over a list that is already drawn would say the list
  // was lost.
  useWake(() => void load());

  /** `at` is where the refusal goes if there is one - see the state above. */
  async function act(action: () => Promise<unknown>, fallback: string, at: string): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      await action();
      await load();
      onSessionChanged();
    } catch (error) {
      setFailure({ at, text: error instanceof ApiError ? error.message : fallback });
    } finally {
      setBusy(false);
    }
  }

  /** The refusal, drawn beside the tap that earned it and nowhere else. */
  function refusal(at: string) {
    return failure !== null && failure.at === at ? <p className="failure">{failure.text}</p> : null;
  }

  /**
   * Book it, and end up on it.
   *
   * Not back on the list: somebody who has just typed a date wants the thing
   * they have just made - its programme, its answer, whose sermon it is - and
   * the list is one tap back from there. The owner's ask of 2026-08-24.
   *
   * Its own `try` rather than `act`, because it needs two things `act` throws
   * away: what the POST answered, and the list as it is after the reload. The
   * screen is opened off the row and not off the brief - the counts and the
   * answers live on the row. If the reload failed there is no row to open, and
   * then this is `act` exactly: the form is closed and the list stands.
   *
   * The order below is the behavior: the sheet closes **before** the reload is awaited, and
   * the shell is told and the new gathering opened only after it lands.
   *
   * Two things about the refusal. It reads `error.message` and never the code — `request`
   * leaves `ApiError.failure` as whatever the body held, and a Pydantic 422's is an array
   * with no `error` in it at all, which is why that one lands as the bare `Ошибка 422`
   * (`api.ts:104`, `FND-069`). And its accusative comes off `plan.kind`, the form's current
   * selection rather than any stored kind: the segment may have been switched since the
   * sheet opened, and the sentence is about what the person has just tried to book.
   */
  async function add(plan: Plan): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      const created = await api.scheduleSession({
        title: plan.title.trim() === '' ? null : plan.title.trim(),
        // The entered time is local; new Date parses it as local and sends it
        // with an offset, so the server receives the exact moment.
        scheduledStart: new Date(plan.start).toISOString(),
        kind: plan.kind,
      });
      setAdding(false);
      const fresh = await load();
      onSessionChanged();
      const row = (fresh ?? []).find((one) => one.session.session_id === created.session_id);
      if (row !== undefined) onOpen({ mode: 'ahead', item: row });
    } catch (error) {
      setFailure({
        at: 'form',
        text:
          error instanceof ApiError
            ? error.message
            : `Не удалось запланировать ${words(plan.kind).accusative}`,
      });
    } finally {
      setBusy(false);
    }
  }

  const items = schedule ?? [];
  /**
   * The next rehearsal of this week, while this person is on the list of
   * sermons — C-002 (`FND-1591`). The server sets `is_mine` only on this
   * week's unmarked rehearsals, so the first one ahead is the date the card
   * names.
   */
  const mySermon = (() => {
    const found = items.find(
      (item) => item.sermon !== null && item.sermon.is_mine && !isPast(item),
    );
    if (found === undefined) return null;
    return {
      id: found.session.session_id,
      when: found.session.scheduled_start ?? found.session.effective_start,
    };
  })();
  /**
   * Scheduled, and its time has passed, and today is not why: this is
   * unfinished work rather than the evening's own gathering, and it used to
   * stand under «Сегодня» with nothing but an orange date to say so - which
   * read as "a lot of open rehearsals" rather than as the two or three
   * genuinely unclosed ones it was.
   */
  const unclosed = items.filter((item) => isPast(item) && !isToday(item));
  const today = items.filter((item) => isToday(item));
  const later = items.filter((item) => !isToday(item) && !isPast(item));
  /* The newest one the scheduler closed, which is the one the alert names. The
     history comes back with those first, so this is the head of the list. */
  const machineClosed = review[0] ?? null;
  const running = session ?? null;
  const empty = schedule !== null && items.length === 0 && running === null && historyTotal === 0;
  /* Nothing ahead of a choir that has a past. The state above is a choir that
     has never met, and it is the only one this screen used to draw: a choir
     with two rehearsals behind it and none in front got the «Прошедшие» row and
     then half a screen of nothing, which reads as a list still loading rather
     than as a list that is empty. What it is missing is one sentence - the
     capsule is drawn over a list that has a tail, so the way in is already
     there and the button of the other state would be a second one. */
  const nothingAhead =
    schedule !== null && items.length === 0 && running === null && historyTotal > 0;

  return (
    <>
      {/* The machine reporting itself, at the top of the first tab. Until this
          existed the only sign that a gathering had been closed by the
          scheduler - with every absence in it written by a clock rather than by
          anybody - was a badge inside «Хор», and a regent who does not open
          that tab never learned of it at all. It leads straight to that
          evening's own screen.

          The regent's alone, and explicitly so since the history began loading
          for everybody on 2026-09-07: it is a call to check a roster, which is
          his to check, and a singer reading «пропуски проставила машина» about
          his own choir can do nothing with it but worry. */}
      {/* C-016 (FND-1611): this and the two cards under it answer after the list
          does, from requests of their own, and each used to arrive in one frame at
          the top of the tab and shove the whole list down. They open through
          `Unfold` now. Motion only: the requests behind them are issued exactly as
          before, and `myAttendance` above is the usage counter. */}
      <Unfold open={isRegent && machineClosed !== null}>
        {machineClosed !== null && (
          <button
            className="tile tile--alert card--toned"
            data-tone="danger"
            onClick={() => onOpen({ mode: 'past', item: machineClosed })}
          >
            <WarningIcon size={18} />
            <span className="tile-line">
              {capitalised(words(machineClosed.kind).accusative)}{' '}
              {formatDayMonth(machineClosed.effective_start)} закрыло приложение —{' '}
              <span className="tile-mark">
                {machineClosed.member_count - machineClosed.present_count}{' '}
                {plural(machineClosed.member_count - machineClosed.present_count, [
                  'пропуск',
                  'пропуска',
                  'пропусков',
                ])}
              </span>{' '}
              проставила машина
            </span>
            {needsReviewCount > 1 && (
              <span className="answer" data-tone="late">
                <span className="num">{needsReviewCount}</span>
              </span>
            )}
            <ChevronRightIcon size={20} quiet />
          </button>
        )}
      </Unfold>

      {/* The rule, under the machine's own report: what a clock wrote about a
          whole gathering is the more urgent of the two, and this one is about
          the person reading it. */}
      {/* C-016 (FND-1611): open only when the banner draws anything, or an empty
          fold would stand in the screen's gap. */}
      <Unfold open={mine !== null && ruleApplies(mine)}>
        {mine !== null && (
          <RuleBanner
            mine={mine}
            nextRehearsal={
              items.find((item) => item.session.kind === 'rehearsal' && !isPast(item))?.session
                .scheduled_start ?? null
            }
          />
        )}
      </Unfold>

      {/* What is expected of the person rather than reported to them, so it is
          the accent and not the ink: the card stands for the brothers on the
          list and nobody else sees it. «Не смогу» is the only button on it.
          C-002 (`FND-1591`): it was one brother's date until 2026-10-06. */}
      {/* C-016 (FND-1611): through `Unfold`, as the two above. */}
      <Unfold open={mySermon !== null}>
        {mySermon !== null && (
          <section className="card card--gathering card--toned">
            <p className="label label--tone">Ты в списке на проповедь</p>
            <h2 className="card-title">{formatAppointment(mySermon.when)}</h2>
            <p className="hint">
              Проповедует тот из списка, кто придёт на спевку. Если на этой неделе не получается —
              скажи сейчас, вместо тебя встанет следующий.
            </p>
            <button
              className="btn btn--outline"
              disabled={busy}
              onClick={() => setDeclining(mySermon.id)}
            >
              Не смогу
            </button>
            {refusal(`sermon:${mySermon.id}`)}
          </section>
        )}
      </Unfold>

      {/* Nothing at all yet: the shape of what is coming. Once the list has
          arrived it stays, and a refused reload draws neither of these. */}
      {schedule === null && failure?.at !== 'screen' && <Skeleton />}
      {schedule === null && failure?.at === 'screen' && (
        <Failed subject="schedule" onRetry={() => void load()} />
      )}

      <Settling>
        {empty && !adding && (
          <div className="block">
            <Empty
              fact="В планах пока пусто"
              note={
                isRegent
                  ? 'Спевки и служения появятся здесь, как только их запланируешь.'
                  : 'Когда регент поставит дату, она появится здесь.'
              }
            >
              {/* The one empty state in the app with a button in it, and the
                  reason is on this screen: the «Запланировать» capsule is
                  deliberately not drawn over an empty list, so without this a
                  regent's first gathering would have no way in at all. */}
              {isRegent && (
                <button className="btn btn--outline" onClick={() => setAdding(true)}>
                  Запланировать
                </button>
              )}
            </Empty>
          </div>
        )}

        {nothingAhead && !adding && (
          <div className="block">
            <Empty
              fact="Впереди пока ничего"
              note={
                isRegent
                  ? 'Спевка или служение появятся здесь, как только запланируешь.'
                  : 'Когда регент поставит дату, она появится здесь.'
              }
            />
          </div>
        )}

        {/* Scheduled, its time is gone, and nobody closed it: unfinished work
            rather than the evening ahead, which is what «Сегодня» is for. It
            used to stand there too, told apart only by the orange of its date -
            which read as "a lot of open rehearsals" on a screen that in fact had
            two or three genuinely unclosed ones. */}
        {unclosed.length > 0 && (
          <div className="block">
            <span className="label label--tone" data-tone="danger">
              Не закрыто · <span className="num">{unclosed.length}</span>
            </span>
            <div className="cards">
              {unclosed.map((item) => (
                <Row
                  key={item.session.session_id}
                  tone="overdue"
                  when={item.session.scheduled_start ?? item.session.effective_start}
                  title={item.session.title ?? words(item.session.kind).titled}
                  onOpen={() => onOpen({ mode: 'ahead', item })}
                />
              ))}
            </div>
          </div>
        )}

        {/* Today, and what is running counts as today whatever its date says. */}
        {(running !== null || today.length > 0) && (
          <div className="block">
            <span className="label">Сегодня</span>
            <div className="cards">
              {running !== null && (
                <Row
                  key={running.session_id}
                  tone="soon"
                  when={running.effective_start}
                  title={running.title ?? words(running.kind).titled}
                  live
                  onOpen={() => onOpen({ mode: 'live' })}
                />
              )}
              {today.map((item) => (
                <Row
                  key={item.session.session_id}
                  tone={isPast(item) ? 'overdue' : 'soon'}
                  when={item.session.scheduled_start ?? item.session.effective_start}
                  title={item.session.title ?? words(item.session.kind).titled}
                  onOpen={() => onOpen({ mode: 'ahead', item })}
                />
              ))}
            </div>
          </div>
        )}

        {/* «Дальше», and the word took a while to find. The rest of what is
            ahead had no label at all on purpose: every candidate either repeated
            the dates under it or had to agree in gender with what it labels, and
            this list mixes спевки with участия. The cost was paid on a screen
            with unclosed gatherings and nothing today - the future card then
            stood inside «Не закрыто · 4» as a fifth one, told apart from the
            four only by the colour of its date, which is 10px of gap against 14
            and reads as one list of five.

            «Дальше» passes both tests the others failed: it is an adverb, so it
            agrees with nothing, and it says where in the list this is rather
            than when the gathering is - exactly as «Сегодня» does above it. Its
            own block rather than a second label inside one, so that a gathering
            three weeks out stops standing under the word «Сегодня» when the
            choir happens to have a rehearsal tonight. */}
        {later.length > 0 && (
          <div className="block">
            <span className="label">Дальше</span>
            <div className="cards">
              {later.map((item) => (
                <Row
                  key={item.session.session_id}
                  tone={null}
                  when={item.session.scheduled_start ?? item.session.effective_start}
                  title={item.session.title ?? words(item.session.kind).titled}
                  onOpen={() => onOpen({ mode: 'ahead', item })}
                />
              ))}
            </div>
          </div>
        )}

        {/* The same rows on the other side of today, folded under one row until
            asked for. It was a screen of its own behind two doors on another
            tab, and what it holds is the cards this list already draws - only
            behind rather than ahead, and an archive rather than a work surface:
            it opens to nothing every time, on purpose. */}
        {historyTotal > 0 && (
          <div className="block">
            <button
              type="button"
              className="archive-toggle"
              aria-expanded={historyExpanded}
              onClick={() => setHistoryExpanded((open) => !open)}
            >
              <span className="archive-toggle-label">
                Прошедшие · <span className="num archive-toggle-count">{historyTotal}</span>
              </span>
              <ChevronDownIcon size={18} quiet turned={historyExpanded} />
            </button>
            {/* C-016 (FND-1611): the cards open and close through `Unfold`, the
                pair to the chevron that already turns, instead of cutting in. */}
            <Unfold open={historyExpanded}>
              <div className="cards">
                {/* The red row is the regent's too, and for the banner's reason
                    above: it says «somebody has to look at this», and the
                    somebody is never the singer reading it. Legacy
                    `ScheduleScreen.tsx:662` at `d5aa0fa` — `FND-916`. */}
                {history.map((item) => (
                  <Row
                    key={item.session_id}
                    tone={isRegent && item.needs_review ? 'overdue' : null}
                    when={item.effective_start}
                    title={item.title ?? words(item.kind).titled}
                    past
                    onOpen={() => onOpen({ mode: 'past', item })}
                  />
                ))}
                {history.length < historyTotal && (
                  <button
                    className="btn btn--outline"
                    disabled={historyLoadingMore}
                    onClick={() => void loadMoreHistory()}
                  >
                    Показать ещё
                  </button>
                )}
              </div>
            </Unfold>
          </div>
        )}
      </Settling>

      {/* The form is a sheet over the list rather than a card inside it. It was
          a card, and with the date wheel open it stopped fitting: it pushed the
          list down and took «Сохранить» off the bottom of the phone. What is
          behind it does not move - the darkening is the whole of what happens
          to the list. The sheet itself is `GatheringForm`, shared with the
          screen of one evening: the same three fields change a plan and make
          one.

          It is rendered unconditionally and told `open`, which is not the same as
          rendering it under the condition: `Sheet` holds the mount for 220 ms after `open`
          goes false so the leaving is seen, and a caller that stopped rendering it would
          leave nothing to animate (`Sheet.tsx:64-107`). No `key` — a remount is exactly the
          fix `FND-074` declined. */}
      <GatheringForm
        open={isRegent && adding}
        mode="new"
        initial={blankPlan()}
        held={held}
        busy={busy}
        kindLocked={null}
        failure={failure !== null && failure.at === 'form' ? failure.text : null}
        onSave={(plan) => void add(plan)}
        onClose={() => setAdding(false)}
      />

      {/* The way to a new gathering, and it is not in the list: it used to end
          the «Сегодня» line, so it was the one object on the screen that began
          nowhere near the left edge - and on a choir with a month booked it
          scrolled away with the row it was pinned to. Fixed above the tab bar,
          it is on screen at the top of the list and at the bottom of it. Not on
          an empty one, where the outlined button is already saying it across
          the whole width. */}
      {isRegent && !empty && (
        <button className="fab" onClick={() => setAdding(true)}>
          <span className="fab-word">Запланировать</span>
          <PlusIcon size={14} cropped />
        </button>
      )}

      {/* Stepping off the list cannot be taken back by the same screen — only
          the keeper puts a brother back for the week — so it is asked from the
          same sheet the other irreversible things are. The price carries what
          actually happens rather than a warning. */}
      <Ask
        open={declining !== null}
        title="Сказать «не смогу»?"
        cost={
          <>
            На этой неделе вместо тебя в списке будет следующий брат. Твоё место в очереди
            сохранится.
          </>
        }
        verb="Не смогу"
        busy={busy}
        failure={
          declining !== null && failure !== null && failure.at === `sermon:${declining}`
            ? failure.text
            : null
        }
        onConfirm={() => {
          if (declining === null) return;
          const id = declining;
          void act(
            async () => {
              await api.declineSermon();
              setDeclining(null);
            },
            'Не удалось освободить дату',
            `sermon:${id}`,
          );
        }}
        onClose={() => setDeclining(null)}
      />
    </>
  );
}
